import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Send, MessageSquare } from "lucide-react";
import { cliente, crmError } from "@/lib/crm";

const fmtTime = (d) => (d ? new Date(d).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");

export const ClienteChat = () => {
  const [canali, setCanali] = useState([]);
  const [channel, setChannel] = useState("");
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const endRef = useRef(null);

  useEffect(() => {
    cliente.chats().then((d) => {
      const list = d.canali || [];
      setCanali(list);
      if (list[0]) setChannel(list[0].channel);
    }).catch((e) => setErr(crmError(e)));
  }, []);

  const load = useCallback(() => {
    if (!channel) return;
    cliente.messages(channel).then((d) => setMsgs(d.messaggi || [])).catch((e) => setErr(crmError(e)));
  }, [channel]);

  useEffect(() => {
    setMsgs(null);
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: "nearest" }); }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true); setErr("");
    try {
      const d = await cliente.send(channel, { testo: text.trim() });
      setMsgs((m) => [...(m || []), d.messaggio]);
      setText("");
    } catch (ex) {
      setErr(crmError(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="card-chat" className="rounded-2xl bg-white p-6 shadow-[0_8px_30px_rgba(30,58,95,0.08)]">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-base font-bold text-zr-blue md:text-lg">
          <MessageSquare className="h-4 w-4 text-zr-green" /> Chat
        </h2>
        {canali.length > 1 && (
          <div className="flex gap-2">
            {canali.map((c) => (
              <button key={c.channel} data-testid={`chat-channel-${c.channel}`} onClick={() => setChannel(c.channel)} className={`rounded-xl px-3 py-1.5 text-xs font-semibold transition-colors ${channel === c.channel ? "bg-zr-blue text-white" : "bg-zr-light text-zr-blue"}`}>
                {c.label}{c.unread ? ` (${c.unread})` : ""}
              </button>
            ))}
          </div>
        )}
        {canali.length === 1 && <span className="text-xs font-semibold text-zr-grey">{canali[0].label}</span>}
      </div>

      <div data-testid="chat-messages" className="h-80 space-y-3 overflow-y-auto rounded-xl bg-zr-bg p-4">
        {msgs === null && !err && <Loader2 className="mx-auto mt-10 h-6 w-6 animate-spin text-zr-green" />}
        {msgs?.length === 0 && <p className="mt-10 text-center text-sm text-zr-grey">Nessun messaggio. Scrivi al tuo advisor qui sotto.</p>}
        {msgs?.map((m) => {
          const mine = m.from === "cliente";
          return (
            <div key={m.id} data-testid="chat-message" className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-zr-blue text-white" : "bg-white text-zr-blue shadow-sm"}`}>
                {!mine && <p className="mb-0.5 text-[11px] font-bold text-zr-green">{m.from_name}</p>}
                <p className="whitespace-pre-wrap break-words">{m.testo}</p>
                <p className={`mt-1 text-[10px] ${mine ? "text-white/70" : "text-[#90A4AE]"}`}>{fmtTime(m.created_at)}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <form onSubmit={send} className="mt-4 flex gap-2">
        <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi un messaggio…" className="flex-1 rounded-xl border border-[#CFD8DC] px-4 py-3 text-sm text-zr-blue outline-none focus:border-zr-green" />
        <button type="submit" disabled={busy || !text.trim()} data-testid="chat-send" className="inline-flex items-center gap-2 rounded-xl bg-zr-green px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#43A047] disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </form>
      {err && <p data-testid="chat-error" className="mt-3 text-sm font-medium text-[#C62828]">{err}</p>}
    </section>
  );
};
